import type { App } from '@keboola/api-client/developerPortal/types';

import type { ComponentTypeOrOther } from './vocabulary';

/** Human labels for the component types. The **values** live in `vocabulary.ts`. */
export const COMPONENT_TYPE_LABELS: Record<ComponentTypeOrOther, string> = {
  extractor: 'Data source (Extractor)',
  writer: 'Data destination (Writer)',
  transformation: 'Transformation',
  application: 'Application',
  processor: 'Processor',
  'code-pattern': 'Code pattern',
  'data-app': 'Data app',
  other: 'Other',
};

/** The two stages the catalog can filter on. */
export const STAGE_LABELS = {
  beta: 'Beta',
  experimental: 'Experimental',
} as const;

/** External developer documentation. */
export const RESOURCES: readonly { title: string; link: string }[] = [
  {
    title: 'Docker Extension Quick Start',
    link: 'https://developers.keboola.com/extend/component/tutorial/',
  },
  { title: 'Common Interface', link: 'https://developers.keboola.com/extend/common-interface/' },
  {
    title: 'About Docker',
    link: 'https://developers.keboola.com/extend/component/docker-tutorial/',
  },
  { title: 'Component Registration', link: 'https://developers.keboola.com/extend/publish/' },
  {
    title: 'Component Deployment',
    link: 'https://developers.keboola.com/extend/component/deployment/',
  },
];

/**
 * The homepage's "Hardest working components" row.
 *
 * - Hand-curated marketing list, not API data; nothing recomputes it.
 * - A component dropped from the catalog still renders here — when editing, check each
 *   `https://apps-api.keboola.com/apps/<id>` still answers 200.
 */
export const FEATURED_APPS: readonly Pick<App, 'id' | 'name' | 'shortDescription' | 'icon'>[] = [
  {
    id: 'keboola.ag-geocoding',
    name: 'Geocoding Augmentation',
    shortDescription: 'Geocoding of locations to GPS coordinates or reversely',
    icon: {
      '128':
        'https://ui.keboola-assets.com/developer-portal/icons/keboola.ag-geocoding/128/1666646174217.png',
    },
  },
  {
    id: 'keboola.ex-google-analytics-v4',
    name: 'Google Analytics',
    shortDescription:
      'Get the data you need to make intelligent marketing and business decisions with Google Analytics.',
    icon: {
      '128':
        'https://ui.keboola-assets.com/developer-portal/icons/keboola.ex-google-analytics-v4/128/1666644437445.png',
    },
  },
  {
    id: 'keboola.ex-google-drive',
    name: 'Google Drive',
    shortDescription: 'Online document and file storage',
    icon: {
      '128':
        'https://ui.keboola-assets.com/developer-portal/icons/keboola.ex-google-drive/128/1666644482955.png',
    },
  },
  {
    id: 'keboola.wr-google-sheets',
    name: 'Google Sheets',
    shortDescription: 'Online spreadsheet storage',
    icon: {
      '128':
        'https://ui.keboola-assets.com/developer-portal/icons/keboola.wr-google-sheets/128/1666644536092.png',
    },
  },
  {
    id: 'keboola.ex-db-mysql',
    name: 'MySQL',
    shortDescription: "World's most popular open source database",
    icon: {
      '128':
        'https://ui.keboola-assets.com/developer-portal/icons/keboola.ex-db-mysql/128/1666644582092.png',
    },
  },
  {
    id: 'keboola.ex-db-pgsql',
    name: 'PostgreSQL',
    shortDescription: "The world's most advanced open source database.",
    icon: {
      '128':
        'https://ui.keboola-assets.com/developer-portal/icons/keboola.ex-db-pgsql/128/1666644620792.png',
    },
  },
  {
    id: 'keboola.wr-redshift-v2',
    name: 'Redshift',
    shortDescription: 'Redshift data warehouse',
    icon: {
      '128':
        'https://ui.keboola-assets.com/developer-portal/icons/keboola.wr-redshift-v2/128/1666647240905.png',
    },
  },
  {
    id: 'keboola.wr-db-snowflake',
    name: 'Snowflake',
    shortDescription: 'Cloud-Native Elastic Data Warehouse Service',
    icon: {
      '128':
        'https://ui.keboola-assets.com/developer-portal/icons/keboola.wr-db-snowflake/128/1666647435037.png',
    },
  },
];

/** How many components a catalog page shows. */
export const CATALOG_PAGE_SIZE = 16;
